import React, { forwardRef } from 'react';
import { Bar } from 'react-chartjs-2';

// Official asset class colors - aligned with Portfolio Reports "Historical
// Allocation" (asset class group) scheme. Kept in sync with generator.py.
// Asset classes with no equivalent in that scheme (Private Debt, Real Assets,
// EM Equities) retain their existing factsheet colours.
export const ASSET_CLASS_COLORS = {
  'Global Alpha': '#BBCBD7',
  'Dynamic Plus Fund': '#BBCBD7',
  'Australian Equities': '#011E41',
  'International Equities': '#5E819C',
  'Real Estate': '#A6C6C1',
  'Infrastructure': '#337B91',
  'Cash': '#EFE6D5',
  'Government Bonds': '#F3BD48',
  'Corporate Debt': '#AA8090',
  'Commodities': '#4B3219',
  'Private Debt': '#874564',
  'Private Equity': '#6C757D',
  'Real Assets (Private Market)': '#127080',
  'EM Equities': '#7B68EE',
};

const FALLBACK_COLORS = ['#072040', '#6c757d', '#212529', '#dc3545', '#28a745'];

// Fixed display order matching factsheet layout
const DISPLAY_ORDER = [
  'Global Alpha',
  'Australian Equities',
  'International Equities',
  'Real Estate',
  'Infrastructure',
  'Cash',
  'Government Bonds',
  'Corporate Debt',
  'EM Equities',
  'Private Debt',
  'Private Equity',
  'Real Assets (Private Market)',
];

export const getAssetClassColor = (assetClass, index) => {
  return ASSET_CLASS_COLORS[assetClass] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

const AllocationBarChart = forwardRef(({ allocation }, ref) => {
  if (!allocation || allocation.length === 0) {
    return <div className="chart-placeholder-text">No allocation data</div>;
  }

  // Sort allocation by fixed display order
  const sorted = [...allocation].sort((a, b) => {
    const idxA = DISPLAY_ORDER.indexOf(a.asset_class);
    const idxB = DISPLAY_ORDER.indexOf(b.asset_class);
    return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
  });

  const colors = sorted.map((a, idx) => getAssetClassColor(a.asset_class, idx));

  const chartData = {
    labels: sorted.map(a => a.asset_class),
    datasets: [{
      data: sorted.map(a => a.weight),
      backgroundColor: colors,
      borderWidth: 0,
      barPercentage: 0.75,
      categoryPercentage: 0.85,
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'rect',
          boxWidth: 10,
          boxHeight: 10,
          padding: 10,
          font: {
            size: 8,
          },
          color: '#333333',
          generateLabels: (chart) => {
            const data = chart.data;
            return data.labels.map((label, i) => ({
              text: label,
              fillStyle: data.datasets[0].backgroundColor[i],
              strokeStyle: 'transparent',
              lineWidth: 0,
              pointStyle: 'rect',
              hidden: false,
              index: i,
            }));
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw.toFixed(1)}%`
        }
      }
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `${value}%`,
          font: {
            size: 8,
          },
          color: '#333333',
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        border: {
          display: false,
        },
      }
    }
  };

  return (
    <div style={{ width: '100%', height: '220px' }}>
      <Bar ref={ref} data={chartData} options={options} />
    </div>
  );
});

export default AllocationBarChart;
